import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Recycle, Truck, Broom, Wrench, RoadHorizon, Factory, CheckCircle } from '@phosphor-icons/react';
import axios from 'axios';
import { Seo } from '../components/Seo';
import { serviceSlug } from '../components/ServicesBentoGrid';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Recycle: Recycle,
  Truck: Truck,
  Broom: Broom,
  Wrench: Wrench,
  Road: RoadHorizon,
  Factory: Factory,
};

const defaultServices = [
  {
    id: '1',
    title: 'Commercial Waste Collections',
    description: 'Professional & Reliable Recycling and Waste Collection Services to Businesses across North Somerset and the Surrounding Area. Solutions Tailored to your Business Needs.',
    icon: 'Recycle',
    image_url: 'https://images.unsplash.com/photo-1717667745830-de42bb75a4fa?crop=entropy&cs=srgb&fm=jpg&q=85',
    features: ['Separate dry recycling collections', 'General waste collections', 'Food waste collections', 'Flexible collection frequencies'],
  },
  {
    id: '2',
    title: 'Driver Training Academy',
    description: 'Unlock your potential and advance your career in the transport industry with our Driver Training Academy. Professional team dedicated to providing high-quality courses.',
    icon: 'Truck',
    image_url: 'https://images.unsplash.com/photo-1759662232707-f1e0a4134c93?crop=entropy&cs=srgb&fm=jpg&q=85',
    features: ['HGV driver training', 'Driver CPC training', 'Professional instructors', 'Flexible scheduling'],
  },
  {
    id: '3',
    title: 'Event Cleansing Services',
    description: 'We understand the importance of maintaining a clean and welcoming environment for your events. Our Event Cleansing Services ensure every aspect of your event shines.',
    icon: 'Broom',
    image_url: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=1000',
    features: ['Corporate conferences', 'Festivals', 'Special occasions', 'Complete waste management'],
  },
  {
    id: '4',
    title: 'Commercial Vehicle Repair',
    description: 'Our Workshop provides competitive, reliable, and cost-effective Commercial Vehicle Maintenance and Repair to keep your wheels and business moving forward.',
    icon: 'Wrench',
    image_url: 'https://images.unsplash.com/photo-1767340078306-89630b12d81c?crop=entropy&cs=srgb&fm=jpg&q=85',
    features: ['Fleet servicing', 'MOT testing', 'Breakdown repairs', 'Preventive maintenance'],
  },
  {
    id: '5',
    title: 'Highways Maintenance',
    description: 'From pothole repairs to full carriageway repair, our Highways team provides reliable, high value, and cost-effective Highways & Civils Services across North Somerset.',
    icon: 'Road',
    image_url: 'https://images.unsplash.com/photo-1584464491033-06628f3a6b7b?q=80&w=1000',
    features: ['Pothole repairs', 'Carriageway repairs', 'Civil engineering', 'Road maintenance'],
  },
  {
    id: '6',
    title: 'Waste Transfer & Disposal',
    description: 'We understand the challenges faced by Commercial Collection providers. Our site is designed to support you in managing waste sustainably while reducing your carbon footprint.',
    icon: 'Factory',
    image_url: 'https://images.unsplash.com/photo-1754797007532-14d15c2eacdb?crop=entropy&cs=srgb&fm=jpg&q=85',
    features: ['Efficient tipping', 'Responsible disposal', 'Carbon footprint reduction', 'Sustainable practices'],
  },
];

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data || []);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  // Smooth-scroll to the section indicated by `location.hash` once the
  // target element actually exists in the DOM. We poll briefly (services
  // come from an API so the section may render a beat after mount) and
  // then smooth-scroll exactly once. scroll-mt-32 on each section handles
  // the sticky header offset so the title lands just below the nav.
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
      return;
    }
    const id = location.hash.replace('#', '');
    let cancelled = false;
    let attempts = 0;

    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        // requestAnimationFrame to ensure layout is settled
        requestAnimationFrame(() => {
          if (!cancelled) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        return;
      }
      attempts += 1;
      if (attempts < 40) setTimeout(tryScroll, 50);  // up to 2s
    };
    tryScroll();
    return () => { cancelled = true; };
  }, [location.hash, services]);

  return (
    <main className="pt-24 bg-white" data-testid="services-page">
      <Seo title="Our Services" description="Commercial waste, driver training, event cleansing, vehicle repair, highways maintenance and waste transfer. Tailored to the South West." path="/services" />
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="accent-font text-lime-600 mb-4">Our Services</p>
            <h1 className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl text-neutral-900 tracking-tight mb-6">
              Comprehensive <span className="text-lime-600">Solutions</span> for Your Business
            </h1>
            <p className="text-neutral-600 text-lg max-w-3xl">
              At NSEC, we offer a range of services tailored to meet your needs. 
              As a local company, we&apos;re always invested in the well-being of our community.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-24">
            {loading ? (
              [0, 1, 2].map(i => (
                <div key={i} className="grid lg:grid-cols-2 gap-12 items-center animate-pulse">
                  <div className="aspect-[4/3] rounded-2xl bg-neutral-100" />
                  <div className="space-y-4">
                    <div className="h-7 w-3/4 bg-neutral-100 rounded" />
                    <div className="h-4 w-full bg-neutral-100 rounded" />
                    <div className="h-4 w-5/6 bg-neutral-100 rounded" />
                    <div className="h-4 w-2/3 bg-neutral-100 rounded mt-6" />
                    <div className="h-4 w-1/2 bg-neutral-100 rounded" />
                  </div>
                </div>
              ))
            ) : services.length === 0 ? (
              <div className="text-center py-16 text-neutral-500">No services available.</div>
            ) : services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Recycle;
              const isEven = index % 2 === 0;
              
              return (
                <motion.div
                  key={service.id}
                  id={serviceSlug(service.title)}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6 }}
                  className={`grid lg:grid-cols-2 gap-12 items-center scroll-mt-32 ${isEven ? '' : 'lg:flex-row-reverse'}`}
                  data-testid={`service-detail-${serviceSlug(service.title)}`}
                >
                  {/* Image */}
                  <div className={`relative ${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
                    <div className="relative overflow-hidden rounded-2xl shadow-xl">
                      <img
                        src={service.image_url}
                        alt={service.title}
                        className="w-full aspect-[4/3] object-cover"
                      />
                    </div>
                    {/* Floating Icon */}
                    <div className="absolute -bottom-6 -right-6 p-4 bg-lime-500 rounded-2xl shadow-lg">
                      {service.icon_url ? (
                        <img src={service.icon_url} alt="" className="w-12 h-12 object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
                      ) : (
                        <IconComponent size={48} weight="duotone" className="text-white" />
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
                    <h2 className="font-outfit font-bold text-2xl lg:text-3xl text-neutral-900 mb-4">
                      {service.title}
                    </h2>
                    <p className="text-neutral-600 leading-relaxed mb-6">
                      {service.description}
                    </p>
                    
                    {/* Features */}
                    <ul className="space-y-3 mb-8">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-3 text-neutral-600">
                          <CheckCircle size={20} weight="fill" className="text-lime-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to="/contact"
                      className="btn-primary inline-flex items-center gap-2"
                      data-testid={`service-cta-${service.id}`}
                    >
                      Get a Quote
                      <ArrowRight size={20} weight="bold" />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
      <section className="py-24 bg-neutral-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-outfit font-bold text-3xl lg:text-4xl text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-neutral-300 text-lg mb-8">
              Contact us today for a free, no-obligation quote. Our friendly team is here to help 
              you find the perfect solution for your business.
            </p>
            <Link
              to="/contact"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="services-page-cta"
            >
              Contact Us Today
              <ArrowRight size={20} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
